import { Grid2 } from "@mui/material";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import { useModelState } from "@/lib/modelstate";
import { authenticationApi, tenantsApi } from "@/boxhub-api";
import { AppPaths } from "@/app/AppPaths";
import Typography from "@mui/material/Typography";
import { CompanyInvitationDTO, Language, TenantDTO } from "@/api/data-contracts";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { IconButtonEx } from "@/lib/components/buttons/IconButtonEx";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import { languageOptions, salutationOptions, useAvailableLanguages } from "@/datacaches";
import { Constants } from "@/api/constants";
import { useTranslation } from "react-i18next";
import { AdminI18N, CommonI18N, CompanyI18N, UserI18N } from "@/translations";
import { DialogEx } from "@/lib/components/DialogEx";
import { ButtonEx } from "@/lib/components/buttons/ButtonEx";
import { KopierenIcon } from "@/agentur/AgenturIcons";
import { useTheme } from "@mui/material/styles";
import { useCompanyInvitationSchema } from "@/pages/company-overview-page/companyInvitationSchema";
import { toLanguageSelectOptions } from "@/pages/user-page/languageSelectOptions";
import { useSnackbarEx } from "@/lib/snackbarex";

export type CompanyInvitationDialogProps = {
    open: boolean;
    close: () => void;
};

export const CompanyInvitationDialog = ({ open, close }: CompanyInvitationDialogProps) => {
    const { t } = useTranslation();
    const { showMessage } = useSnackbarEx();
    const [tenants, setTenants] = useState<TenantDTO[]>([]);

    useEffect(() => {
        const loadTenants = async () => {
            try {
                const response = await tenantsApi.getAllTenants();
                setTenants(response.data);
            } catch (error: unknown) {
                setTenants([]);
                showMessage({ summary: t(CommonI18N.message.loadFailed), severity: "error", error });
            }
        };
        void loadTenants();
    }, [showMessage, t]);

    const tenantOptions = useMemo(
        () => tenants.map((tenant) => ({ label: tenant.name ?? "", value: tenant.id })),
        [tenants]
    );

    const salutationSelectOptions = useMemo(
        () =>
            salutationOptions.map((option) => ({
                label: option && t(CommonI18N.enum.salutation[option]),
                value: option
            })),
        [t]
    );

    const [availableLanguages] = useAvailableLanguages();
    // While `useAvailableLanguages()` is loading (or permanently, on a fetch error) it resolves
    // to `[]` — fall back to the static baseline so the dropdown isn't empty and the schema's
    // `.oneOf(...)` (see companyInvitationSchema.ts) never validates against an empty set.
    const effectiveLanguages = availableLanguages.length > 0 ? availableLanguages : languageOptions;
    const languageSelectOptions = useMemo(
        () => toLanguageSelectOptions(effectiveLanguages, t),
        [effectiveLanguages, t]
    );
    const schema = useCompanyInvitationSchema(effectiveLanguages);
    const theme = useTheme();
    const { model, errors, onChange, save } = useModelState<CompanyInvitationDTO>({
        initialState: { language: Language.GERMAN } as CompanyInvitationDTO,
        schema,
        saver: authenticationApi.generateCompanySignUpInvitation
    });

    const edit = model.invitationToken == null;

    const buttons = (
        <>
            <Grid2>
                <ButtonEx
                    label={edit ? t(CommonI18N.action.cancel) : t(CommonI18N.action.close)}
                    color="secondary"
                    onClick={close}
                />
            </Grid2>
            {edit && (
                <Grid2>
                    <ButtonEx
                        label={t(AdminI18N.companies.action.inviteSubmit)}
                        color="primary"
                        disabled={model.email == null}
                        onClick={() => save()}
                    />
                </Grid2>
            )}
        </>
    );

    return (
        <DialogEx
            open={open}
            onClose={close}
            maxWidth="sm"
            fullWidth
            title={t(CompanyI18N.form.page.inviteTitle)}
            buttons={buttons}
        >
            <Grid2 container spacing={2} padding={2} width="100%">
                {edit ? (
                    <>
                        <Grid2 width="100%">
                            <StringInputField
                                name="companyName"
                                value={model?.companyName}
                                label={t(CompanyI18N.form.field.name)}
                                error={errors?.companyName}
                                maxChars={Constants.COMPANY_NAME_LENGTH_MAX}
                                onChange={onChange}
                            />
                        </Grid2>
                        <Grid2 width="100%">
                            <SelectInputField
                                name="tenantId"
                                label={t(AdminI18N.companies.field.tenant)}
                                options={tenantOptions}
                                value={model.tenantId}
                                onChange={onChange}
                                error={errors.tenantId}
                                displayEmpty={true}
                            />
                        </Grid2>
                        <Grid2 width="100%">
                            <SelectInputField
                                name="salutation"
                                label={t(UserI18N.form.field.salutation)}
                                value={model?.salutation}
                                error={errors?.salutation}
                                options={salutationSelectOptions}
                                displayEmpty={true}
                                onChange={onChange}
                            />
                        </Grid2>

                        <Grid2 width="100%">
                            <StringInputField
                                name="firstName"
                                label={t(UserI18N.form.field.firstName)}
                                value={model?.firstName}
                                error={errors?.firstName}
                                maxChars={Constants.FIRSTNAME_LENGTH_MAX}
                                onChange={onChange}
                            />
                        </Grid2>
                        <Grid2 width="100%">
                            <StringInputField
                                name="lastName"
                                label={t(UserI18N.form.field.lastName)}
                                value={model?.lastName}
                                error={errors?.lastName}
                                maxChars={Constants.LASTNAME_LENGTH_MAX}
                                onChange={onChange}
                            />
                        </Grid2>
                        <Grid2 width="100%">
                            <StringInputField
                                name="email"
                                value={model?.email}
                                label={t(UserI18N.form.field.email)}
                                error={errors?.email}
                                maxChars={Constants.EMAIL_LENGTH_MAX}
                                onChange={onChange}
                            />
                        </Grid2>
                        <Grid2 width="100%">
                            <SelectInputField
                                name="language"
                                label={t(UserI18N.form.field.language)}
                                value={model?.language}
                                error={errors?.language}
                                options={languageSelectOptions}
                                displayEmpty={true}
                                onChange={onChange}
                            />
                        </Grid2>
                    </>
                ) : (
                    <Grid2 container direction="row" spacing={2}>
                        <Grid2>
                            <Typography variant="h6">{t(AdminI18N.companies.message.invitationSent)}</Typography>
                        </Grid2>
                        <Grid2>
                            <IconButtonEx
                                icon={<KopierenIcon mode={theme.palette.mode} />}
                                size="small"
                                onClick={() =>
                                    navigator.clipboard.writeText(
                                        `${globalThis.location.origin}#${AppPaths.COMPANY_SIGNUP_fn(model.invitationToken!)}`
                                    )
                                }
                                title={t(AdminI18N.companies.action.copyLink)}
                            />
                        </Grid2>
                    </Grid2>
                )}
            </Grid2>
        </DialogEx>
    );
};
